import {
  RentalAgreementDriverCreateModel,
  RentalAgreementDriverDetailsModel
} from './rental-agreement-drivers/rental-agreement-drivers.model';
import {
  RentalAgreementValueCreateModel,
  RentalAgreementValueDetailsModel
} from './rental-agreement-values/rental-agreement-values.model';

export interface RentalAgreementListModel {
  uid: string;
  entityVersion: string;
  reservationNumber: string;
  number: string;
  referenceNumber: string;
  status: RentalAgreementStatus;
  driverFirstName: string;
  driverLastName: string;
  locationOut: string;
  locationIn: string;
  dateOut: string;
  dateIn: string;
  clientNumber: string;
  createdDate: string;
}

export interface RentalAgreementDetailsModel {
  uid: string;
  version: number;
  number: string;
  reservationNumber: string;
  status: RentalAgreementStatus;
  dateOut: string;
  dateIn: string;
  closeDate: string | null;
  pickupLocationUid: string;
  reservationUid: string | null;
  dropOffLocationUid: string;
  vehicleCategoryUid: string;
  servedVehicleCategoryUid: string;
  brokerClientId: number;
  numberOfDaysToInvoice: number;
  numberOfDaysToInvoiceCashier: number;
  numberOfDays: number;
  brokerReference: string;
  rate: string;
  ratePeriodId: number;
  observations: string;
  currency: string;
  taxRate: number;
  totalTaxValue: number;
  cdwExcess: number;
  tpExcess: number;
  areKmsIncluded: boolean;
  areKmsFreePerDay: boolean;
  freeKmsPerDay: number;
  additionalKmsPrice: number;
  discountAmount: number;
  brokerOrigin: string;
  mainDriver: RentalAgreementDriverDetailsModel | null;
  totalCashier: number;
  totalCashierWithTax: number;
  totalCashierTaxValue: number;
  totalInvoice: number;
  totalInvoiceWithTax: number;
  totalInvoiceTaxValue: number;
  totalValue: number;
  totalValueWithTax: number;
  createdDate: string;
  createdBy: string;
  modifiedDate: string;
  modifiedBy: string;
  // Children delivered with the details, so the client doesn't need separate requests for them.
  drivers: RentalAgreementDriverDetailsModel[];
  valueItems: RentalAgreementValueDetailsModel[];
}

export interface RentalAgreementCreateModel {
  reservationNumber: string;
  status: RentalAgreementStatus;
  dateOut: string;
  dateIn: string;
  pickupLocationUid: string;
  dropOffLocationUid: string;
  vehicleCategoryUid: string;
  servedVehicleCategoryUid: string;
  brokerClientId: number;
  numberOfDays: number;
  brokerReference: string;
  rate: string;
  ratePeriodId: number;
  observations: string;
  currency: string;
  taxRate: number;
  totalTaxValue: number;
  cdwExcess: number;
  tpExcess: number;
  areKmsIncluded: boolean;
  areKmsFreePerDay: boolean;
  freeKmsPerDay: number;
  additionalKmsPrice: number;
  discountAmount: number;
  drivers: RentalAgreementDriverCreateModel[];
  valueItems: RentalAgreementValueCreateModel[];
}

export interface RentalAgreementUpdateModel {
  uid: string;
  dateIn: string;
  observations: string;
  status: RentalAgreementStatus;
  servedVehicleCategoryUid?: string;
  // Full driver list (uid = empty guid for new drivers). Omitted while the current drivers
  // haven't been staged yet, so a save that doesn't touch them leaves them untouched.
  drivers?: RentalAgreementDriverUpdatePayload[];
  // Full value item list (uid = empty guid for new items). Same omission rule as drivers.
  valueItems?: RentalAgreementValueDetailsModel[];
}

export interface RentalAgreementDriverUpdatePayload extends Omit<RentalAgreementDriverDetailsModel, 'uid'> {
  uid: string;
}

export enum RentalAgreementStatus {
  RaOpen = 0,
  RaClosed = 1,
  Invoiced = 2,
  PaidByBroker = 3,
  PaidToCashier = 4
}

export const RentalAgreementStatusesDisplayNames = ['Open', 'Closed', 'Invoiced', 'Paid By Broker', 'Paid To Cashier'];

export interface RentalAgreementFilter {
  reservationNumber?: string | null;
  rentalAgreementNumber?: string | null;
  reference?: string | null;
  driverFirstName?: string | null;
  driverLastName?: string | null;
  clientId?: number | null;
  pickupLocations?: string | null;
  dropOffLocations?: string | null;
  dateOutFrom?: string | null;
  dateOutTo?: string | null;
  dateInFrom?: string | null;
  dateInTo?: string | null;
  creationDateFrom?: string | null;
  creationDateTo?: string | null;
  statuses?: RentalAgreementStatus[] | null;
}

export type RaVersion = number | 'latest';
